/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

import { CryptoJS } from '@quickplay/foundation';
import { v4 as uuid } from 'uuid';

export const GODAVARI_GVUID_STORAGE_KEY = 'GVUID';

/** MD5 digest (UTF-8) as 32 hex characters, matching the id format of Sony's web SDK. */
export function hashToGodavariId(input: string): string {
  return CryptoJS.MD5(input).toString(CryptoJS.enc.Hex);
}

function now(): number {
  return typeof performance === 'undefined' ? 0 : performance.now();
}

/**
 * Returns the persistent Godavari device id (`as-gvuid`), creating it on first use:
 * hash of `<uuid>-<epoch ms>-<performance.now()>`, kept in localStorage. Falls back to a
 * per-call id when storage is unavailable.
 */
export function getOrCreateGodavariGvuid(storageKey: string = GODAVARI_GVUID_STORAGE_KEY): string {
  const create = () => hashToGodavariId(`${uuid()}-${Date.now()}-${now()}`);
  try {
    const existing = localStorage.getItem(storageKey);
    if (existing) return existing;
    const created = create();
    localStorage.setItem(storageKey, created);
    return created;
  } catch {
    return create();
  }
}

/** Creates a Godavari session id (`as-gvsid`): hash of `<GVUID>-<epoch ms>`. */
export function createGodavariSessionId(gvuid: string, timestamp: number = Date.now()): string {
  return hashToGodavariId(`${gvuid}-${timestamp}`);
}
